import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, Mail } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Product, ROUTE_PATHS } from '@/lib/index';
import { springPresets, hoverLift } from '@/lib/motion';

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const productDetailPath = ROUTE_PATHS.PRODUCT_DETAIL.replace(':id', product.id);
  const supplierDetailPath = ROUTE_PATHS.SUPPLIER_DETAIL.replace(':id', product.supplierId);

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full flex flex-col overflow-hidden border-border bg-card hover:shadow-lg transition-shadow duration-200">
        <CardHeader className="p-0">
          <Link to={productDetailPath} className="block relative aspect-[4/3] overflow-hidden bg-muted">
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            />
            {product.featured && (
              <Badge className="absolute top-3 right-3 bg-accent text-accent-foreground">
                Featured
              </Badge>
            )}
          </Link>
        </CardHeader>

        <CardContent className="flex-1 p-6 space-y-3">
          <div className="space-y-2">
            <Badge variant="secondary" className="text-xs">
              {product.category}
            </Badge>
            <Link to={productDetailPath}>
              <h3 className="text-lg font-semibold text-foreground hover:text-primary transition-colors line-clamp-2">
                {product.name}
              </h3>
            </Link>
          </div>

          <Link
            to={supplierDetailPath}
            className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
          >
            by {product.supplierName}
            <ExternalLink className="w-3 h-3" />
          </Link>

          <p className="text-sm text-muted-foreground line-clamp-3">
            {product.shortDescription}
          </p>
        </CardContent>

        <CardFooter className="p-6 pt-0 flex gap-2">
          <Button asChild className="flex-1" variant="default">
            <Link to={productDetailPath}>View Details</Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="icon"
            className="shrink-0"
          >
            <a
              href={`mailto:info@airportsecurity.asia?subject=Enquiry about ${product.name}`}
              aria-label="Make Enquiry"
            >
              <Mail className="w-4 h-4" />
            </a>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
